import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Shield,
  GitBranch,
  Wallet,
  Users,
  CheckCircle2,
  ChevronRight,
  Terminal,
  Plug,
  MessageSquare,
  ArrowRight,
  Eye,
  FileText,
  Lock,
  BarChart3,
  Zap,
  CreditCard
} from "lucide-react";
import ArchitectureDiagram from "../components/ArchitectureDiagram";
import SwarmCrewSection from "../components/SwarmCrewSection";

const platformFeatures = [
  {
    category: "Governance",
    icon: Shield,
    color: "swarm-violet",
    features: [
      { name: "Policy Engine", description: "Define rules that agents must follow" },
      { name: "Approval Quorums", description: "Multi-signature agent actions" },
      { name: "Agent Identity (KYA)", description: "Know Your Agent verification" },
      { name: "Audit Trail", description: "Complete action history" },
      { name: "Compliance Mapping", description: "Regulatory framework alignment" }
    ]
  },
  {
    category: "Orchestration",
    icon: GitBranch,
    color: "swarm-cyan",
    features: [
      { name: "Workflow Builder", description: "Visual agent workflow design" },
      { name: "OpsControl", description: "Centralized command dashboard" },
      { name: "Task Routing", description: "Intelligent work distribution" },
      { name: "Agent Management", description: "Lifecycle administration" },
      { name: "Real-time Monitoring", description: "Live operational visibility" }
    ]
  },
  {
    category: "Treasury",
    icon: Wallet,
    color: "swarm-green",
    features: [
      { name: "Distribution Mode", description: "Revenue sharing automation" },
      { name: "Budget Control", description: "Spending limits and caps" },
      { name: "Tap Rules", description: "Transaction velocity controls" },
      { name: "Revenue Engine", description: "Value-generating swarm payouts" },
      { name: "CFO Dashboard", description: "Financial oversight console" }
    ]
  }
];

const cisoFeatures = [
  { icon: Lock, title: "Compliance Scoring", description: "Real-time posture assessment across frameworks" },
  { icon: FileText, title: "Audit Reports", description: "Auto-generated compliance documentation" },
  { icon: Eye, title: "Threat Detection", description: "Behavioral anomaly identification" },
  { icon: BarChart3, title: "Risk Analytics", description: "Quantified risk metrics and trending" }
];

const registrationMethods = [
  {
    id: "gateway",
    name: "API Gateway",
    icon: Plug,
    tagline: "Zero Code Change",
    description: "Enterprise-grade integration that intercepts agent API calls without modifying your existing codebase.",
    features: ["Zero deployment changes", "Instant governance", "Full audit trail", "Policy enforcement"],
    code: null,
    diagram: true
  },
  {
    id: "sdk",
    name: "SDK Integration",
    icon: Terminal,
    tagline: "5 Lines of Code",
    description: "Developer-friendly SDK for direct integration into your agent framework.",
    features: ["Python & TypeScript", "Full API access", "Budget-aware context", "Event hooks"],
    code: `from swarmcitadel import Agent, Policy

# Initialize your governed agent
agent = Agent(api_key="sk_...")
agent.register(policy=Policy.STANDARD)

# Execute with governance
result = await agent.execute(action="trade", params={...})`
  },
  {
    id: "assisted",
    name: "Assisted Onboarding",
    icon: MessageSquare,
    tagline: "AI Native",
    description: "Chat with our SwarmCrew agents to configure governance for your swarm automatically.",
    features: ["Conversational setup", "Auto-configuration", "Policy recommendations", "Guided walkthrough"],
    code: null
  }
];

export const ProductPage = () => {
  return (
    <div className="pt-24" data-testid="product-page">
      <Seo title="Platform: Policy Engine, Audit Trails & Labor Attribution | SwarmCitadel" description="Explore the SwarmCitadel platform: runtime policy enforcement, tamper-evident audit trails, treasury controls and labor attribution for autonomous AI agents." path="/product" />
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-swarm-violet/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="section-label">Platform</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4">
              Institutional Infrastructure
            </h1>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
              <span className="text-gradient-safe">for Agent Operations</span>
            </h1>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              SwarmCitadel provides the governance, orchestration, and treasury layers 
              that transform autonomous agents into enterprise-ready operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-glow" data-testid="product-cta-demo">
                  Request Demo <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/docs">
                <Button variant="outline" size="lg" className="btn-outline">
                  API Reference
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Screenshots / Dashboard Preview */}
      <section className="py-16 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="section-label">Dashboard</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">See It In Action</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              OpsControl gives you real-time visibility into every agent, policy, and transaction.
            </p>
          </div>

          {/* Dashboard Mockups */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* OpsControl View */}
            <div className="glass-card p-4 border-swarm-violet/30">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <span className="text-slate-500 text-xs ml-2">OpsControl</span>
              </div>
              <div className="bg-swarm-darker rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">Active Swarms</span>
                  <span className="text-swarm-green font-mono">12 running</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {["Trading Alpha", "SEM Optimizer", "Support Bot"].map((name, i) => (
                    <div key={name} className="bg-swarm-violet/10 rounded-lg p-3 border border-swarm-violet/20">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-swarm-green animate-pulse" />
                        <span className="text-white text-sm">{name}</span>
                      </div>
                      <div className="text-slate-500 text-xs">{[847, 234, 1203][i]} actions/hr</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-swarm-border">
                  <span className="text-slate-400 text-sm">Total Actions Today</span>
                  <span className="text-swarm-cyan font-mono font-bold">24,847</span>
                </div>
              </div>
            </div>

            {/* Governance Policies Panel */}
            <div className="glass-card p-4 border-swarm-cyan/30">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <span className="text-slate-500 text-xs ml-2">Governance Policies</span>
              </div>
              <div className="bg-swarm-darker rounded-lg p-6 space-y-3">
                {[
                  { name: "Max Daily Spend", rule: "≤ $10,000/day", status: "active" },
                  { name: "Velocity Cap", rule: "≤ 100 tx/hour", status: "active" },
                  { name: "Approved Wallets", rule: "3 destinations", status: "active" },
                  { name: "High-Value Approval", rule: "> $5k requires 2-sig", status: "active" }
                ].map((policy) => (
                  <div key={policy.name} className="flex items-center justify-between py-2 border-b border-swarm-border last:border-0">
                    <div>
                      <span className="text-white text-sm">{policy.name}</span>
                      <span className="text-slate-500 text-xs ml-2">{policy.rule}</span>
                    </div>
                    <span className="px-2 py-1 bg-swarm-green/10 text-swarm-green text-xs rounded">
                      {policy.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Swarm Management */}
            <div className="glass-card p-4 border-swarm-green/30">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <span className="text-slate-500 text-xs ml-2">Swarm Management</span>
              </div>
              <div className="bg-swarm-darker rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-medium">DeFi Trading Swarm</span>
                  <span className="px-2 py-1 bg-swarm-green/10 text-swarm-green text-xs rounded-full">Healthy</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-slate-500 text-xs mb-1">Budget Used</div>
                    <div className="text-white font-mono">$4,231 / $10,000</div>
                    <div className="w-full h-1.5 bg-swarm-border rounded mt-2">
                      <div className="w-[42%] h-full bg-swarm-cyan rounded" />
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-slate-500 text-xs mb-1">Success Rate</div>
                    <div className="text-swarm-green font-mono font-bold">99.2%</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-swarm-violet/10 text-swarm-violet text-xs rounded">5 agents</span>
                  <span className="px-2 py-1 bg-swarm-cyan/10 text-swarm-cyan text-xs rounded">12 policies</span>
                </div>
              </div>
            </div>

            {/* Treasury View */}
            <div className="glass-card p-4 border-swarm-violet/30">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <span className="text-slate-500 text-xs ml-2">Treasury Dashboard</span>
              </div>
              <div className="bg-swarm-darker rounded-lg p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Total Treasury</div>
                    <div className="text-2xl font-bold text-white font-mono">$847,231</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Revenue (30d)</div>
                    <div className="text-2xl font-bold text-swarm-green font-mono">+$19,751</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Distributed to agents</span>
                    <span className="text-white font-mono">$12,847</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Held in reserve</span>
                    <span className="text-white font-mono">$6,904</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Architecture */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Architecture</div>
            <h2 className="section-title">Three-Layer Stack</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              A complete platform for institutional-grade agent governance. 
              Click to explore each layer.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto mb-16">
            <ArchitectureDiagram />
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
            {platformFeatures.map((category) => {
              const Icon = category.icon;
              const colorClasses = {
                'swarm-violet': { bg: 'bg-swarm-violet/10', text: 'text-swarm-violet', border: 'border-swarm-violet/30' },
                'swarm-cyan': { bg: 'bg-swarm-cyan/10', text: 'text-swarm-cyan', border: 'border-swarm-cyan/30' },
                'swarm-green': { bg: 'bg-swarm-green/10', text: 'text-swarm-green', border: 'border-swarm-green/30' }
              };
              const colors = colorClasses[category.color];

              return (
                <div key={category.category} className={`glass-card p-6 border ${colors.border}`}>
                  <div className={`p-3 rounded-xl ${colors.bg} ${colors.text} w-fit mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{category.category}</h3>
                  <ul className="space-y-3">
                    {category.features.map((feature) => (
                      <li key={feature.name} className="flex items-start gap-2">
                        <CheckCircle2 className={`w-4 h-4 ${colors.text} mt-1 flex-shrink-0`} />
                        <div>
                          <span className="text-white text-sm font-medium">{feature.name}</span>
                          <p className="text-slate-500 text-xs">{feature.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Governance Engine Section - PROMINENT */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-swarm-violet/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="section-label text-swarm-violet">Policy Engine</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Institutional-Grade Policy Control
              <br />
              <span className="text-gradient-safe">for Autonomous Agents</span>
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto text-lg">
              Precise control over who can move what, where, and under which conditions. 
              Every action evaluated before execution.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Capabilities */}
            <div className="glass-card p-8 border-swarm-violet/30">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Zap className="w-5 h-5 text-swarm-violet" />
                Capabilities
              </h3>
              <ul className="space-y-4">
                {[
                  "10 rule operators, composable JSON rules",
                  "4 conflict resolution strategies (most restrictive wins)",
                  "Policy dry-run: simulate without executing",
                  "Per-agent, per-role, per-transaction, per-time-window controls",
                  "Guided builder for non-technical operators"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-swarm-violet flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What It Governs */}
            <div className="glass-card p-8 border-swarm-cyan/30">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-swarm-cyan" />
                What It Governs
              </h3>
              <ul className="space-y-4">
                {[
                  "Spending limits (per-tx, daily, weekly, monthly caps)",
                  "Velocity checks (too many actions = automatic pause)",
                  "Destination allowlists (approved wallets and accounts only)",
                  "Category restrictions (trading agent can't pay vendors)",
                  "Approval quorums (multi-sig for high-value actions)"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-swarm-cyan flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-swarm-border">
                <Link to="/x402" className="text-swarm-cyan hover:underline text-sm flex items-center gap-1">
                  See how this applies to x402 agent payments
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* C-Suite Dashboards */}
          <div className="mt-12">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">C-Suite Dashboards</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { role: "CEO", desc: "Cross-swarm KPIs, agent health, company metrics", color: "swarm-violet" },
                { role: "CFO", desc: "Treasury aggregation, revenue, cost tracking", color: "swarm-green" },
                { role: "COO", desc: "Operations efficiency, workflow throughput", color: "swarm-cyan" },
                { role: "CISO", desc: "Global governance posture, compliance monitoring", color: "swarm-violet" }
              ].map((dashboard) => (
                <div key={dashboard.role} className={`glass-card p-4 border-${dashboard.color}/20`}>
                  <div className={`text-${dashboard.color} font-bold text-lg mb-1`}>{dashboard.role}</div>
                  <p className="text-slate-400 text-sm">{dashboard.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Connectors Section */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label text-swarm-cyan">Connectors</div>
            <h2 className="section-title">Platform & Financial Connectors</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Where agents work and how agents pay. Two connector categories, one unified governance layer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Platform Connectors */}
            <div className="glass-card p-8 border-swarm-cyan/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-swarm-cyan/10">
                  <Plug className="w-6 h-6 text-swarm-cyan" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Platform Connectors</h3>
                  <span className="text-swarm-cyan text-sm font-mono">39 integrations today</span>
                </div>
              </div>
              <p className="text-slate-400 mb-6">Where agents work.</p>
              <div className="flex flex-wrap gap-2">
                {["Shopify", "Google Ads", "Meta Ads", "SAP", "Uniswap", "Polymarket", "Stripe", "Salesforce", "HubSpot"].map((connector) => (
                  <span key={connector} className="px-3 py-1 bg-swarm-cyan/10 text-swarm-cyan text-sm rounded-full">
                    {connector}
                  </span>
                ))}
                <span className="px-3 py-1 bg-white/5 text-slate-400 text-sm rounded-full">
                  +30 more
                </span>
              </div>
            </div>

            {/* Financial Connectors */}
            <div className="glass-card p-8 border-swarm-green/30 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-2 py-1 bg-swarm-green/10 text-swarm-green text-xs rounded-full">
                Coming Soon
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-swarm-green/10">
                  <CreditCard className="w-6 h-6 text-swarm-green" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Financial Connectors</h3>
                  <span className="text-swarm-green text-sm font-mono">How agents pay</span>
                </div>
              </div>
              <p className="text-slate-400 mb-6">How your agents pay and get paid.</p>
              <ul className="space-y-3">
                {[
                  "Virtual cards for agents (per-agent spending limits)",
                  "Bank accounts (ACH, wire, international)",
                  "Mass payouts (batch disbursements, multi-currency)",
                  "Crypto on/off ramps"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-slate-500 text-sm mt-4 italic">
                Every financial action governed by the same policy engine. Same audit trail. Same approval chains.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SwarmCrew */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <SwarmCrewSection />
          
          {/* Agent Definition Model */}
          <div className="mt-16 glass-card p-8 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Agent Architecture</h3>
            <p className="text-slate-400 mb-6">
              Every SwarmCrew agent operates with three core components that define their behavior and capabilities:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-swarm-violet/10 rounded-lg border border-swarm-violet/20">
                <code className="text-swarm-violet text-sm font-mono">SOUL.md</code>
                <p className="text-slate-400 text-xs mt-2">Core identity and purpose definition</p>
              </div>
              <div className="p-4 bg-swarm-cyan/10 rounded-lg border border-swarm-cyan/20">
                <code className="text-swarm-cyan text-sm font-mono">HEARTBEAT.md</code>
                <p className="text-slate-400 text-xs mt-2">Operational rhythm and triggers</p>
              </div>
              <div className="p-4 bg-swarm-green/10 rounded-lg border border-swarm-green/20">
                <code className="text-swarm-green text-sm font-mono">TOOLS.md</code>
                <p className="text-slate-400 text-xs mt-2">Available capabilities and APIs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agent Registration Methods */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Integration</div>
            <h2 className="section-title">Agent Registration</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Three paths to governance. Choose the integration that fits your workflow.
            </p>
          </div>

          <Tabs defaultValue="gateway" className="max-w-5xl mx-auto">
            <TabsList className="grid grid-cols-3 bg-swarm-darker border border-swarm-border rounded-lg p-1 mb-8">
              {registrationMethods.map((method) => (
                <TabsTrigger
                  key={method.id}
                  value={method.id}
                  className="data-[state=active]:bg-swarm-violet/20 data-[state=active]:text-swarm-violet rounded-md py-3 transition-all"
                  data-testid={`tab-${method.id}`}
                >
                  <method.icon className="w-4 h-4 mr-2" />
                  {method.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {registrationMethods.map((method) => (
              <TabsContent key={method.id} value={method.id}>
                <div className="glass-card p-8">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="p-4 rounded-xl bg-swarm-violet/10">
                      <method.icon className="w-8 h-8 text-swarm-violet" />
                    </div>
                    <div>
                      <div className="text-swarm-cyan text-sm font-mono mb-2">{method.tagline}</div>
                      <h3 className="text-2xl font-bold text-white mb-2">{method.name}</h3>
                      <p className="text-slate-400">{method.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {method.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {method.code && (
                    <div className="bg-swarm-darker rounded-lg p-6 font-mono text-sm overflow-x-auto">
                      <pre className="text-slate-300">
                        <code>{method.code}</code>
                      </pre>
                    </div>
                  )}

                  {method.diagram && (
                    <div className="bg-swarm-darker rounded-lg p-6 text-center">
                      <div className="flex items-center justify-center gap-4 text-sm">
                        <div className="px-4 py-2 bg-swarm-violet/10 rounded-lg text-swarm-violet">Your Agent</div>
                        <ArrowRight className="w-5 h-5 text-slate-500" />
                        <div className="px-4 py-2 bg-swarm-cyan/10 rounded-lg text-swarm-cyan">API Gateway</div>
                        <ArrowRight className="w-5 h-5 text-slate-500" />
                        <div className="px-4 py-2 bg-swarm-green/10 rounded-lg text-swarm-green">SwarmCitadel</div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CISO Agent Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="section-label text-swarm-green">For Security Leaders</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                The CISO Agent
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                Purpose-built for institutional buyers. Real-time compliance posture, 
                regulatory mapping, and automated audit reports that make the CFO say yes.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {cisoFeatures.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div key={feature.title} className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-swarm-green/10">
                        <Icon className="w-5 h-5 text-swarm-green" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{feature.title}</h4>
                        <p className="text-slate-500 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass-card p-8 border-swarm-green/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-swarm-green/10">
                  <Shield className="w-8 h-8 text-swarm-green" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Why the CFO Says Yes</h3>
                  <p className="text-slate-500 text-sm">Quantified risk, automated compliance</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "Auto-freeze on policy violations",
                  "SOC2, GDPR, HIPAA mapping",
                  "Board-ready audit reports",
                  "Real-time risk scoring"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to see it in action?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Schedule a demo to see OpsControl, policy engine, and treasury controls live.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-glow" data-testid="product-bottom-cta">
              Request Demo <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
